import * as React from "react";

import { MetricDefinitionRecord, Period } from "@/shared/domain/metrics";

import { getCardTitle } from "../cardTitle";
import {
  ChartCardConfig,
  effectiveChartMetric,
} from "../DashboardHeader/DashboardParams";

import { BucketWindow } from "./bucketWindow";
import { DrillDownDialog } from "./DrillDownDialog";
import { MetricChart } from "./MetricChart";
import styles from "./MetricChartCard.module.css";

export interface MetricChartCardProps {
  definitions: MetricDefinitionRecord[];
  /** Configuration for this card (controlled by the parent). */
  config: ChartCardConfig;
  /** This card's position among the dashboard's unresolved cards — spreads
   *  seeded (metric-less) cards across distinct definitions (see
   *  {@link effectiveChartMetric}). */
  seedIndex?: number;
  orgId: string | undefined;
  period: Period;
  startTimeNs: number;
  endTimeNs: number;
  deviceIds?: string[];
  sessionIds?: string[];
}

/** The bucket the user drilled into, pinned with the metric/period it was
 *  opened at so the dialog stays stable if the card re-resolves meanwhile. */
interface DrilldownSelection {
  metricName: string;
  bucket: BucketWindow;
  period: Period;
}

/**
 * Full-width chart card: a compact `aggregation of metric` title above the
 * chart, plus the drill-down dialog opened by clicking a chart bucket. Pure
 * display — the metric/aggregation/display selection is edited through the
 * card's ⋮ → "Edit Data" popover (see {@link CardEditPopover}), owned by the
 * parent that owns the {@link ChartCardConfig}.
 *
 * The drill-down dialog is local component state (per card, so only the
 * drilled card opens); it closes on unmount, e.g. when navigating away.
 */
export function MetricChartCard({
  definitions,
  config,
  seedIndex = 0,
  orgId,
  period,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
}: MetricChartCardProps) {
  const names = definitions.map((d) => d.name);

  const selectedName = effectiveChartMetric(config, names, seedIndex);
  const aggregation = config.aggregation;
  const display = config.display;

  const [drill, setDrill] = React.useState<DrilldownSelection | null>(null);

  const openDrill = React.useCallback(
    (bucket: BucketWindow) =>
      setDrill({ metricName: selectedName, bucket, period }),
    [selectedName, period],
  );

  const closeDrill = React.useCallback(() => setDrill(null), []);

  if (names.length === 0) {
    return null;
  }

  const titleText = getCardTitle(selectedName, aggregation);

  return (
    <div className={styles.card}>
      <div className={styles.title} title={titleText}>
        {titleText}
      </div>

      <div className={styles.chartArea}>
        <MetricChart
          metricName={selectedName}
          orgId={orgId}
          period={period}
          aggregation={aggregation}
          startTimeNs={startTimeNs}
          endTimeNs={endTimeNs}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
          display={display}
          onBucketClick={openDrill}
        />
      </div>

      <DrillDownDialog
        open={drill !== null}
        onClose={closeDrill}
        metricName={drill?.metricName ?? selectedName}
        period={drill?.period ?? period}
        bucket={drill?.bucket ?? null}
        orgId={orgId}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
      />
    </div>
  );
}
